import React, { useState, useMemo } from 'react'
import { Modal, Button, Checkbox, Icon, Empty } from 'antd'
import './FinalVideoSelection.scss'

const FinalVideoSelection = ({ visible, scenes, onCancel, onConfirm }) => {
  // State to store selected shot IDs. By default, select all valid shots (with vidSrc)
  const defaultSelectedIds = useMemo(() => {
    const ids = new Set()
    scenes.forEach(scene => {
      scene.shots.forEach(shot => {
        if (shot.vidSrc) ids.add(shot.id)
      })
    })
    return ids
  }, [scenes])

  const [selectedShotIds, setSelectedShotIds] = useState(defaultSelectedIds)

  const toggleShot = (shotId) => {
    const newSelected = new Set(selectedShotIds)
    if (newSelected.has(shotId)) {
      newSelected.delete(shotId)
    } else {
      newSelected.add(shotId)
    }
    setSelectedShotIds(newSelected)
  }

  const toggleScene = (scene, checked) => {
    const newSelected = new Set(selectedShotIds)
    scene.shots.forEach(shot => {
      if (shot.vidSrc) {
        if (checked) {
          newSelected.add(shot.id)
        } else {
          newSelected.delete(shot.id)
        }
      }
    })
    setSelectedShotIds(newSelected)
  }

  const handleContinue = () => {
    // Filter scenes to only include selected shots
    const filteredScenes = scenes.map(scene => ({
      ...scene,
      shots: scene.shots.filter(shot => selectedShotIds.has(shot.id))
    })).filter(scene => scene.shots.length > 0)
    
    onConfirm(filteredScenes)
  }

  const canContinue = selectedShotIds.size > 0

  return (
    <Modal
      visible={visible}
      title={null}
      width="90%"
      style={{ top: 20, maxWidth: '1200px' }}
      centered
      onCancel={onCancel}
      footer={null}
      bodyStyle={{ padding: 0, background: '#fafafa', borderRadius: '8px' }}
      destroyOnClose
      getContainer={false}
    >
      <div className="final-video-selection">
        <div className="final-video-selection__header">
        <h1 className="final-video-selection__title">
          <Icon type="video-camera" style={{ marginRight: 12, color: '#1890ff' }} />
          Select Final Clips
        </h1>
        <p className="final-video-selection__subtitle">
          Choose which scenes and shots you want to include in your final video.
          Unselected clips will be excluded from the generated video.
        </p>
      </div>

      <div className="final-video-selection__content">
        {scenes.length === 0 ? (
          <Empty description="No generated shots available" />
        ) : (
          scenes.map((scene, sceneIndex) => {
            const validShots = scene.shots.filter(s => s.vidSrc)
            if (validShots.length === 0) return null

            const isAllSelected = validShots.every(s => selectedShotIds.has(s.id))
            const isSomeSelected = validShots.some(s => selectedShotIds.has(s.id)) && !isAllSelected

            return (
              <div key={scene.id} className="final-video-selection__scene">
                <div className="final-video-selection__scene-header">
                  <div className="final-video-selection__scene-title">
                    {scene.title || `Scene ${sceneIndex + 1}`}
                  </div>
                  <Checkbox 
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={(e) => toggleScene(scene, e.target.checked)}
                  >
                    Select All in Scene
                  </Checkbox>
                </div>

                <div className="final-video-selection__shots">
                  {validShots.map((shot, shotIndex) => {
                    const isSelected = selectedShotIds.has(shot.id)
                    return (
                      <div 
                        key={shot.id} 
                        className={`final-video-selection__shot-card ${isSelected ? 'final-video-selection__shot-card--selected' : ''}`}
                        onClick={() => toggleShot(shot.id)}
                      >
                        {shot.imageSrc ? (
                          <img src={shot.imageSrc} alt="Shot preview" className="final-video-selection__shot-image" />
                        ) : (
                          <div className="final-video-selection__shot-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon type="picture" style={{ fontSize: 24, color: '#9ca3af' }} />
                          </div>
                        )}
                        <div className="final-video-selection__shot-info">
                          <span className="final-video-selection__shot-title">
                            {shot.title || `Shot ${shotIndex + 1}`}
                          </span>
                          <Checkbox checked={isSelected} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="final-video-selection__footer">
        <Button size="large" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="primary" 
          size="large" 
          onClick={handleContinue}
          disabled={!canContinue}
        >
          Open Video Editor
          <Icon type="right" />
        </Button>
        </div>
      </div>
    </Modal>
  )
}

export default FinalVideoSelection
